import { Link } from 'react-router-dom';
import type { NewsItem } from '@/types/news';

type FeedListProps = {
  items: NewsItem[];
  visible: number;
  onLoadMore: () => void;
};

const tagTone = [
  'bg-primary-100 text-primary-400',
  'bg-accent-100 text-accent-400',
  'bg-secondary-100 text-secondary-400',
];

export default function FeedList({ items, visible, onLoadMore }: FeedListProps) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-search-eye-line text-2xl" />
        </span>
        <p className="mt-4 text-sm text-foreground-600">
          没有找到匹配的内容，换个关键词或切换圈子试试。
        </p>
      </div>
    );
  }

  const shown = items.slice(0, visible);

  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col gap-4">
        {shown.map((item, index) => (
          <li key={item.id}>
            <article
              className="rise-in group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-400/60"
              style={{ animationDelay: `${Math.min(index, 6) * 40}ms` }}
            >
              <Link to={`/article/${item.id}`} className="flex flex-col sm:flex-row">
                <div className="relative h-44 w-full shrink-0 overflow-hidden sm:h-auto sm:w-[228px]">
                  <img
                    src={item.cover}
                    alt={`${item.title} 配图`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-md bg-background-50/85 px-2 py-1 text-[11px] font-label text-primary-400 backdrop-blur">
                    {item.categoryName}
                  </span>
                </div>
                <div className="flex min-w-0 flex-1 flex-col p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-history-line" />
                      {item.time}
                    </span>
                    <span className="hidden items-center gap-1 sm:flex">
                      <i className="ri-user-line" />
                      {item.author}
                    </span>
                  </div>
                  <h3 className="mt-3 font-heading text-xl leading-snug text-foreground-950 transition-colors group-hover:text-primary-400">
                    {item.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                    {item.excerpt}
                  </p>
                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
                    {item.tags.map((tag, i) => (
                      <span
                        key={tag}
                        className={`rounded-md px-2 py-1 text-[11px] font-label ${tagTone[i % tagTone.length]}`}
                      >
                        {tag}
                      </span>
                    ))}
                    <span className="ml-auto flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1 text-accent-500">
                        <i className="ri-fire-line" />
                        {(item.heat / 10000).toFixed(1)}万
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line" />
                        {item.comments}
                      </span>
                    </span>
                  </div>
                </div>
              </Link>
            </article>
          </li>
        ))}
      </ul>

      {visible < items.length ? (
        <button
          type="button"
          onClick={onLoadMore}
          className="mx-auto mt-2 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-6 py-3 text-sm font-label text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-400"
        >
          <i className="ri-add-line" />
          加载更多（剩余 {items.length - visible} 条）
        </button>
      ) : (
        <p className="py-2 text-center text-xs text-foreground-500">已展示当前筛选下的全部内容</p>
      )}
    </div>
  );
}